import React from 'react';
import { Reorder, useDragControls, useReducedMotion } from 'framer-motion';
import '../../assets/styles/ReorderList.css';

/* Lista reordenable con asa de arrastre.
 *
 * Dos diferencias con el original de referencia, y las dos por accesibilidad:
 *
 * - Alli la fila entera es el elemento arrastrable y lleva role="button" con
 *   el contenido en aria-hidden. Eso vale para filas de solo texto, pero las
 *   nuestras llevan casilla y botones dentro: quedarian ocultos al lector de
 *   pantalla y anidados dentro de un boton, que no es HTML valido. Aqui
 *   arrastra solo el asa, asi que el contenido sigue siendo interactivo.
 * - El asa tambien mueve la fila con las flechas del teclado, para quien no
 *   pueda arrastrar. El original resolvia esto sobre la fila entera.
 *
 * Reorder y useDragControls salen de framer-motion, que ya estaba instalado;
 * el paquete `motion` es el mismo proyecto renombrado.
 */

const SPRING = { type: 'spring', stiffness: 520, damping: 34, mass: 0.45 };
const INSTANT = { duration: 0 };

const Grip = () => (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" aria-hidden="true">
    <circle cx="2.5" cy="2.5" r="1.2" />
    <circle cx="7.5" cy="2.5" r="1.2" />
    <circle cx="2.5" cy="7" r="1.2" />
    <circle cx="7.5" cy="7" r="1.2" />
    <circle cx="2.5" cy="11.5" r="1.2" />
    <circle cx="7.5" cy="11.5" r="1.2" />
  </svg>
);

const ReorderRow = ({ item, label, position, total, onMove, reduced, children, gripLabel }) => {
  const controls = useDragControls();
  const [isDragging, setIsDragging] = React.useState(false);

  const onKeyDown = (event) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    onMove(event.key === 'ArrowUp' ? -1 : 1);
  };

  return (
    <Reorder.Item
      value={item}
      dragListener={false}
      dragControls={controls}
      transition={reduced ? INSTANT : SPRING}
      whileDrag={reduced ? undefined : { scale: 1.02 }}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={() => setIsDragging(false)}
      className={`reorder__row ${isDragging ? 'is-dragging' : ''}`}
    >
      <button
        type="button"
        className="reorder__grip"
        aria-label={`${gripLabel}: ${label} (${position} / ${total})`}
        onPointerDown={(event) => controls.start(event)}
        onKeyDown={onKeyDown}
      >
        <Grip />
      </button>

      <div className="reorder__content">{children}</div>
    </Reorder.Item>
  );
};

const ReorderList = ({ items, getId, getLabel, onReorder, renderItem, label, gripLabel = 'Reordenar' }) => {
  const reduced = useReducedMotion() === true;

  const move = (id, delta) => {
    const from = items.findIndex((item) => getId(item) === id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= items.length) return;
    const next = [...items];
    const [taken] = next.splice(from, 1);
    next.splice(to, 0, taken);
    onReorder(next);
  };

  return (
    <Reorder.Group axis="y" values={items} onReorder={onReorder} aria-label={label} className="reorder">
      {items.map((item, index) => (
        <ReorderRow
          key={getId(item)}
          item={item}
          label={getLabel(item)}
          position={index + 1}
          total={items.length}
          onMove={(delta) => move(getId(item), delta)}
          reduced={reduced}
          gripLabel={gripLabel}
        >
          {renderItem(item)}
        </ReorderRow>
      ))}
    </Reorder.Group>
  );
};

export default ReorderList;
